import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@brimostech/ui';

interface Props {
  children: ReactNode;
  /** Where the boundary sits, for the console line (e.g. "página"). */
  scope?: string;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence for a render error. Without it React 19 unmounts the whole tree and the
 * user sees a blank page (it happened with a dialog in the operator area); with it the broken
 * screen shows what went wrong and a button to reload, and the rest of the app stays usable.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `Erro ao renderizar ${this.props.scope ?? 'a tela'}:`,
      error,
      info.componentStack,
    );
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        role="alert"
        className="mx-auto my-12 flex max-w-md flex-col items-center gap-3 rounded-xl border border-border bg-card p-8 text-center shadow-sm"
      >
        <AlertTriangle className="size-8 text-destructive" aria-hidden />
        <h2 className="text-lg font-semibold">Algo deu errado nesta tela</h2>
        <p className="text-sm text-muted-foreground">
          Recarregue a página. Se continuar, avise o suporte com a hora e o que você estava fazendo.
        </p>
        <p className="font-mono text-xs text-muted-foreground">{error.message}</p>
        <Button onClick={() => window.location.reload()}>Recarregar</Button>
      </div>
    );
  }
}
